
import React, { useState, useEffect, useRef } from "react";

// Keep in sync with the 3-tag limit enforced in ProjectTagModal.
const MAX_TAGS = 3;

// `readOnly` renders the tags without the Add Tag pill and without opening the
// tag modal — used for projects shared with the current user, where only the
// project owner may change tags.
const DynamicTags = ({ tags, openTagModalFor, itemId, isApplying = false, readOnly = false }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(1);

  const tagList = Array.isArray(tags) ? tags : [];
  const canAddMore = !readOnly && tagList.length < MAX_TAGS;

  const openModal = (e) => {
    e.stopPropagation();
    if (readOnly) return;
    openTagModalFor(itemId);
  };

  useEffect(() => {
    if (!containerRef.current || !measureRef.current || tagList.length === 0) return;

    const observer = new ResizeObserver(() => {
      if (!containerRef.current || !measureRef.current) return;
      const containerWidth = containerRef.current.clientWidth;
      const widths = Array.from(measureRef.current.children).map(
        (el) => el.getBoundingClientRect().width
      );

      const gap = 4;            // matches the visible row's gap-1
      const morePillWidth = 30; // approx width of the "+N" pill
      const addPillWidth = 86;  // approx width of the "Add Tag +" pill
      const addReserve = canAddMore ? gap + addPillWidth : 0;

      // If every tag fits alongside the Add Tag pill, show them all.
      const totalWidth =
        widths.reduce((sum, w) => sum + w, 0) + gap * (widths.length - 1);
      if (totalWidth + addReserve <= containerWidth) {
        setVisibleCount(widths.length);
        return;
      }

      // Otherwise, fit as many as possible while reserving room for "+N"
      // (and the Add Tag pill, when more tags can still be added).
      let currentWidth = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i++) {
        const sep = count > 0 ? gap : 0;
        if (
          currentWidth + sep + widths[i] + gap + morePillWidth + addReserve <=
          containerWidth
        ) {
          count++;
          currentWidth += sep + widths[i];
        } else {
          break;
        }
      }

      setVisibleCount(Math.max(1, count));
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, canAddMore]);

  // While the project list is refreshing after a tag change, show a
  // non-interactive applying state instead of the (stale) add/pills view.
  if (isApplying) {
    return (
      <span className="inline-flex items-center justify-center gap-1 px-3.5 py-1 rounded-full border border-[#E4D4F5] text-[#A259FF] bg-[#F8F3FF] text-[13px] font-medium font-outfit cursor-default animate-pulse">
        Applying…
      </span>
    );
  }

  const addTagPill = (
    <span
      className="inline-flex items-center justify-center gap-1 px-3 py-[2px] rounded-full border border-[#BB42AE] text-[#BB42AE] bg-white hover:bg-[#FBF1FA] text-[12px] font-medium font-outfit cursor-pointer transition-all flex-shrink-0 whitespace-nowrap"
      onClick={openModal}
    >
      Add Tag <span className="text-[14px] font-light leading-none">+</span>
    </span>
  );

  // No tags yet — just the Add Tag pill (or a placeholder when read-only).
  if (tagList.length === 0) {
    return readOnly ? (
      <span className="text-[#52525B] font-outfit text-[15px] font-medium">_</span>
    ) : (
      addTagPill
    );
  }

  return (
    <div
      className={`relative flex items-center w-full h-full group/tags ${readOnly ? "cursor-default" : ""}`}
      onClick={openModal}
    >
      <div ref={containerRef} className="flex gap-1 items-center w-full overflow-hidden">
        {tagList.slice(0, visibleCount).map((tag) => (
          <span
            key={tag.id}
            className={`${readOnly ? "" : "cursor-pointer"} px-[10px] py-[2px] rounded-full text-[12px] font-medium truncate min-w-0 text-[#52525B] bg-[#FCFCFC] border border-[#DFDFDF]`}
          >
            {tag.name}
          </span>
        ))}
        {tagList.length > visibleCount && (
          <span className="cursor-pointer px-[8px] py-[2px] rounded-full text-[12px] font-medium text-[#52525B] bg-[#F4F4F5] border border-[#D4D4D8] flex-shrink-0">
            +{tagList.length - visibleCount}
          </span>
        )}
        {canAddMore && addTagPill}
      </div>

      <div
        ref={measureRef}
        className="absolute top-0 left-0 flex gap-1.5 pointer-events-none opacity-0 invisible -z-10"
        aria-hidden="true"
      >
        {tagList.map((tag) => (
          <span
            key={`measure-${tag.id}`}
            className="px-[10px] py-[2px] rounded-full text-[12px] font-medium whitespace-nowrap border"
          >
            {tag.name}
          </span>
        ))}
      </div>
    </div>
  );
};

export default DynamicTags;
